import React, { useRef, useCallback, useEffect } from 'react';
import { useController } from 'react-hook-form';
import { v4 } from 'uuid';

/**
 * TextArea component that wraps the GDS textarea-wrapper web component
 * for use with react-hook-form. Provides form validation and error handling
 * for textarea input fields.
 * 
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.control - React Hook Form control object for form state management
 * @param {string} props.name - The name/key for this field in the form data
 * @param {string} [props.label="Text Area"] - The label text displayed for the input field
 * @param {string} [props.placeholder="Enter your text here..."] - The placeholder text for the textarea
 * @param {string} [props.descriptiveCopy] - Optional descriptive text displayed below the input
 * @param {number} [props.rows] - Number of visible text lines for the textarea
 * @param {number} [props.cols] - Number of visible character columns for the textarea
 * @param {number} [props.maxLength] - Maximum number of characters allowed
 * @returns {JSX.Element} The rendered TextArea component
 * 
 * @example
 * // Basic usage with react-hook-form
 * <TextArea 
 *   control={control} 
 *   name="comments" 
 * />
 * 
 * @example
 * // With custom label and placeholder
 * <TextArea 
 *   control={control} 
 *   name="feedback"
 *   label="Your Feedback"
 *   placeholder="Please share your thoughts..."
 *   rows={5}
 *   maxLength={500}
 * />
 */
export default function TextArea({
    control,
    name,
    label = "Text Area",
    placeholder = "Enter your text here...",
    descriptiveCopy,
    rows,
    cols,
    maxLength,
    showCharacterCount = false,
    characterCountMax = 1000,
    characterCountWarningThreshold = 100,
    characterCountLabel = "Characters Remaining:"
}) {
    /** @type {React.MutableRefObject<HTMLTextAreaElement>} Reference to the textarea element */
    const textareaRef = useRef();

    /**
     * React Hook Form controller for managing field state, validation, and error handling
     * @type {Object} field - Field methods and properties from react-hook-form
     * @type {Object} fieldState - Field state including error information
     */
    const { field, fieldState: { error } } = useController({
        control,
        name
    });

    /**
     * Handles input value changes from the textarea element
     * Updates the form field value when user types in the textarea
     * 
     * @param {Event} e - The change event from the textarea element
     */
    const handleTextareaChange = useCallback((e) => {
        field.onChange(e.target.value);
    }, [field.onChange]);

    /**
     * Effect to set up and clean up event listener for textarea changes
     * Listens to the 'change' and 'input' events from the textarea element
     */
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.addEventListener("change", handleTextareaChange);
            textarea.addEventListener("input", handleTextareaChange);
            return () => {
                textarea.removeEventListener("change", handleTextareaChange);
                textarea.removeEventListener("input", handleTextareaChange);
            };
        }
    }, [handleTextareaChange]);

    /**
     * Generate unique ID for the textarea element
     * @type {string}
     */
    const textareaId = `textarea-${v4()}`;

    return (
        <div>
            <gds-textarea-wrapper
                style={showCharacterCount ?
                    { marginBottom: 'var(--textarea-countdown-margin-bottom)' } :
                    {}
                }
            >
                <gds-label-legend slot="label" element="label" for={textareaId}>
                    {label}
                </gds-label-legend>

                <textarea
                    className='gds-text-area'
                    ref={textareaRef}
                    slot="textarea"
                    id={textareaId}
                    name={name}
                    placeholder={placeholder}
                    value={field.value || ''}
                    onChange={handleTextareaChange}
                    onBlur={field.onBlur}
                    rows={rows}
                    cols={cols}
                    maxLength={maxLength}
                />

                {descriptiveCopy &&
                    <span slot="descriptive-text">{descriptiveCopy}</span>
                }

                {error &&
                    <span slot="error-message">{error.message}</span>
                }
            </gds-textarea-wrapper>

            {showCharacterCount && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginBottom: 'var(--textarea-wrapper-margin-bottom)'
                }}>
                    <gds-countdown
                        current-count={field?.value?.length ?? 0}
                        max-count={characterCountMax}
                        warning-threshold={characterCountWarningThreshold}
                        state="neutral"
                    >
                        <span slot="label">{characterCountLabel}</span>
                    </gds-countdown>
                </div>
            )}
        </div>
    );
}